import { useEffect, useMemo, useState } from 'react';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getGridColumns } from '../../Compounds/UpstackDataGrid/helpers';
import { GridPreferences, getSelectedAccountsSubject, getUserPreferencesSubject, PrefKey } from '../../../store/User';
import { getInventoryChangedSubject } from '../../../store/Inventory';
import { useLocations } from '../../../store/Locations';
import { getErrorByDomainSubject } from '../../../store/Error';
import { RecordUnavailable } from '../../Compounds/RecordUnavailable/RecordUnavailable';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { LOCATIONS_GRID_KEY, locationsGridColumns, toLocationGridRows } from './locationsGridColumnDefs';
import { LocationDrawer } from './LocationDrawer';

export type LocationsTableProps = {
  projectsEnabled: boolean;
};

export function LocationsTable({ projectsEnabled }: LocationsTableProps) {
  const { locations, loading, fetchLocations, refreshLocations } = useLocations();
  const [gridSettings, setGridSettings] = useState<GridPreferences>({} as GridPreferences);
  const [fetchStarted, setFetchStarted] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | undefined>(getErrorByDomainSubject().value.locations);

  useEffect(() => {
    const prefsSub = getUserPreferencesSubject().subscribe((prefs) =>
      setGridSettings((prefs.content[LOCATIONS_GRID_KEY as PrefKey] || {}) as GridPreferences)
    );
    const accountsSub = getSelectedAccountsSubject().subscribe(() => {
      setFetchStarted(true);
      void fetchLocations();
    });
    const changesSub = getInventoryChangedSubject().subscribe(() => void refreshLocations());
    const errorSub = getErrorByDomainSubject().subscribe((errors) => setLoadError(errors.locations));
    return () => {
      prefsSub.unsubscribe();
      accountsSub.unsubscribe();
      changesSub.unsubscribe();
      errorSub.unsubscribe();
    };
  }, []);

  const rows = useMemo(() => toLocationGridRows(locations ?? []), [locations]);
  const columns = useMemo(
    () => getGridColumns(rows, locationsGridColumns(projectsEnabled, rows), LOCATIONS_GRID_KEY),
    [rows, projectsEnabled]
  );

  const loadFailed = fetchStarted && !loading && locations?.length === 0 && !!loadError;

  if (loadFailed) {
    return (
      <RecordUnavailable
        message="The locations could not be loaded."
        actionText="Try again"
        onAction={() => void fetchLocations()}
        dataTestId="locations-unavailable"
      />
    );
  }

  return (
    <>
      <UpstackDataGrid
        dataCy="locations-data-grid"
        columns={columns}
        gridSettings={gridSettings}
        loadingData={!fetchStarted || loading || locations === null}
        page={LOCATIONS_GRID_KEY}
        pinnedColumns={{ left: ['details'] }}
        rows={rows}
        showSearch
        title="Locations"
      />
      {loading && (
        <div className="absolute inset-0 w-full h-full flex items-center justify-center">
          <ComponentSpinner />
        </div>
      )}
      <LocationDrawer locations={rows} />
    </>
  );
}
